'use client';
import { Command, CommandInput, CommandItem, CommandList } from 'cmdk';
import { useState, useEffect, useMemo } from 'react';
import { Search, ChevronDown, Calendar, MessageSquare, FileText, Reply } from 'lucide-react';
import { ICommunity, IExclusiveContent } from '@search/services';
import { LoadingSkeleton } from '@ui/components/atoms';
import { useRouter, useSearchParams } from 'next/navigation';
import { searchRoutes } from '@ui/constants';
import { formattedDateWithDateAgo } from '@ui/components';

interface SearchCommandProps {
  items: (IExclusiveContent | ICommunity)[];
  onClose?: () => void;
  onInputChange?: (value: string) => void;
  placeholder?: string;
  isLoading?: boolean;
  isOpen?: boolean;
  userType?: 'creator' | 'fan';
  searchSet: any;
  searchDomain: 'community' | 'exclusive_content';
  totalValue: number;
}

// Content type filter options
type ContentTypeFilter = 'all' | 'posts' | 'comments' | 'replies';

export const SearchCommand = ({
  items,
  onClose,
  onInputChange,
  placeholder,
  isLoading,
  isOpen,
  searchSet,
  searchDomain,
  userType,
  totalValue,
}: SearchCommandProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const urlQuery = searchParams.get('search_query') || '';
  const [search, setSearch] = useState(urlQuery);
  const [contentTypeFilter, setContentTypeFilter] = useState<ContentTypeFilter>('all');
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);

  // Helper functions
  const getContentTypeIcon = (item: ICommunity | IExclusiveContent) => {
    if (searchDomain === 'exclusive_content') {
      return <FileText className="h-4 w-4 text-blue-500" />;
    }

    // For community items, determine type based on thread_deepness and post property
    if ('post' in item) {
      if (item.post) {
        return <MessageSquare className="h-4 w-4 text-green-500" />;
      } else if (item.thread_deepness === 1) {
        return <Reply className="h-4 w-4 text-orange-500" />;
      } else {
        return <Reply className="h-4 w-4 text-purple-500" />;
      }
    }

    return <MessageSquare className="h-4 w-4 text-gray-500" />;
  };

  const getContentTypeLabel = (item: ICommunity | IExclusiveContent) => {
    if (searchDomain === 'exclusive_content') {
      return 'Content';
    }

    if ('post' in item) {
      if (item.post) {
        return 'Post';
      } else if (item.thread_deepness === 1) {
        return 'Comment';
      } else {
        return 'Reply';
      }
    }

    return 'Community';
  };

  // Keyboard events
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) close();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  // Click outside to close filter dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isFilterDropdownOpen && !(e.target as Element).closest('.filter-dropdown')) {
        setIsFilterDropdownOpen(false);
      }
    };

    if (isFilterDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isFilterDropdownOpen]);

  const updateURL = (query: string) => {
    const params = new URLSearchParams(searchParams);
    query ? params.set('search_query', query) : params.delete('search_query');
    router.replace(`${window.location.pathname}?${params}`, { scroll: false });
  };

  const data = useMemo(() => {
    let filteredItems = Array.isArray(items) && items.length > 0 ? items : [];

    // Apply content type filter
    if (contentTypeFilter !== 'all' && searchDomain === 'community') {
      filteredItems = filteredItems.filter((item) => {
        if ('post' in item) {
          if (contentTypeFilter === 'posts') return item.post;
          if (contentTypeFilter === 'comments') return !item.post && item.thread_deepness === 1;
          if (contentTypeFilter === 'replies') return !item.post && item.thread_deepness > 1;
        }
        return true;
      });
    }

    return filteredItems as (IExclusiveContent | ICommunity)[];
  }, [items, searchDomain, searchSet, contentTypeFilter]);

  const handleSearch = () => {
    if (!search.trim()) return;
    updateURL(search);
    onInputChange?.(search);
  };

  useEffect(() => {
    setSearch(urlQuery);
    if (urlQuery && onInputChange && searchSet) {
      handleSearch();
    }
  }, [searchSet]);

  const close = () => {
    updateURL('');
    setSearch('');
    setContentTypeFilter('all');
    onClose?.();
  };

  const handleSelect = (item: ICommunity | IExclusiveContent) => {
    const isCreator = userType === 'creator';
    const builder = searchRoutes[searchDomain];

    if (!builder) {
      console.warn(`No route mapping for searchDomain: ${searchDomain}`);
      return;
    }

    const url = builder(item, isCreator);
    router.push(url);
  };

  const handleViewAll = (item: ICommunity | IExclusiveContent) => {
    if (userType == 'creator') {
      router.push(
        `/dashboard/search/?search_domain=${searchDomain || 'community'}&search_query=${search}`,
      );
    } else {
      router.push(
        `/creators/${item.creator_display_name}/search?search_domain=${
          searchDomain || 'community'
        }&search_query=${search}&creator_id=${item.creator_id}`,
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/40 backdrop-blur-sm p-3 sm:p-4 pt-16 sm:pt-20 animate-in fade-in duration-200"
      onClick={(e) => e.target === e.currentTarget && close()}>
      <div
        className="w-full max-w-lg sm:max-w-xl z-[9999] lg:max-w-2xl mx-auto rounded-md bg-white shadow-2xl border border-gray-200 animate-in slide-in-from-top-4 duration-300 ease-out overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <Command
          className="w-full bg-white"
          shouldFilter={false}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              handleSearch();
            }
          }}>
          {/* Search Input with Filter */}
          <div className="flex items-center px-4 py-3 border-b border-gray-100">
            <Search className="h-4 w-4 text-gray-400 mr-3 flex-shrink-0" />
            <CommandInput
              autoFocus
              value={search}
              onValueChange={setSearch}
              placeholder={placeholder || 'Search...'}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
            />

            {/* Content Type Filter Dropdown */}
            {/* {searchDomain === 'community' && (
              <div className="relative mr-2 filter-dropdown">
                <button
                  type="button"
                  onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                  className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors border border-gray-200"
                >
                  <span>
                    {contentTypeFilter === 'all' && 'All'}
                    {contentTypeFilter === 'posts' && 'Posts'}
                    {contentTypeFilter === 'comments' && 'Comments'}
                    {contentTypeFilter === 'replies' && 'Replies'}
                  </span>
                  <ChevronDown className={`h-3 w-3 transition-transform ${isFilterDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isFilterDropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                    {(['all', 'posts', 'comments', 'replies'] as ContentTypeFilter[]).map((type) => (
                      <button
                        key={type}
                        onClick={() => {
                          setContentTypeFilter(type);
                          setIsFilterDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 first:rounded-t-md last:rounded-b-md ${
                          contentTypeFilter === type ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {type === 'all' && 'All'}
                        {type === 'posts' && 'Posts'}
                        {type === 'comments' && 'Comments'}
                        {type === 'replies' && 'Replies'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )} */}

            <Search
              onClick={handleSearch}
              className="h-9 w-9 text-muted-foreground cursor-pointer p-1 rounded-md bg-gray-200 hover:bg-gray-300 transition"
            />
          </div>

          {/* Results */}
          <CommandList className="max-h-[60vh] overflow-y-auto scrollbar-hide">
            {isLoading ? (
              <div className="py-2">
                {Array.from({ length: 4 }, (_, i) => (
                  <div
                    key={i}
                    className="px-4 py-3 border-b border-gray-50 last:border-b-0 animate-pulse">
                    <LoadingSkeleton className="h-4 w-3/4 mb-2" />
                    <LoadingSkeleton className="h-3 w-1/2" />
                  </div>
                ))}
              </div>
            ) : data?.length > 0 ? (
              <div className="py-1 my-2">
                {data.map((item, index) => (
                  <CommandItem
                    key={`${item.id}-${index}`}
                    value={`item-${index}`}
                    className="cursor-pointer mx-2 my-1 px-3 py-3 border border-gray-200 rounded-md hover:border-gray-300 hover:shadow-sm transition-all duration-200 ease-out hover:scale-[1.01] active:scale-[0.99] bg-white min-h-[90px]"
                    onSelect={() => handleSelect(item)}>
                    <div className="w-full space-y-3">
                      {/* Header with type icon and label */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          {getContentTypeIcon(item)}
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            {getContentTypeLabel(item)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1 text-xs text-gray-400">
                          <Calendar className="h-3 w-3" />
                          <span>
                            {formattedDateWithDateAgo(
                              searchDomain === 'community'
                                ? item?.created_at
                                : item?.date_of_publish,
                            )}
                          </span>
                        </div>
                      </div>

                      {/* Title */}
                      {item?.name && (
                        <h3
                          className="text-sm font-semibold text-gray-900 leading-tight group-hover:text-blue-600 transition-colors"
                          dangerouslySetInnerHTML={{
                            __html: item.name
                              .replace(
                                /<mark>/g,
                                '<span class="bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded text-sm font-medium">',
                              )
                              .replace(/<\/mark>/g, '</span>'),
                          }}
                        />
                      )}

                      {/* Description */}
                      {item?.description && (
                        <p
                          className="text-xs text-gray-600 leading-relaxed line-clamp-2 group-hover:text-blue-600 transition-colors"
                          dangerouslySetInnerHTML={{
                            __html: item.description
                              .replace(
                                /<mark>/g,
                                '<span class="bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded text-xs font-medium">',
                              )
                              .replace(/<\/mark>/g, '</span>'),
                          }}
                        />
                      )}

                      {/* Footer with creator info */}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        <span className="text-xs text-gray-500 font-medium">
                          {/* by {item.creator_display_name} */}
                          {searchDomain === 'community' ? 'Community' : 'Content'}
                        </span>
                        {searchDomain === 'community' &&
                          'thread_deepness' in item &&
                          item.thread_deepness > 1 && (
                            <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded-full">
                              {/* Level {item.thread_deepness} */}
                            </span>
                          )}
                      </div>
                    </div>
                  </CommandItem>
                ))}

                {/* Compact View All */}
                {totalValue > 10 && (
                  <div className="mx-2 my-1 px-3 py-2.5 border border-gray-200 rounded-md bg-gradient-to-r from-primaryLight-50 to-indigo-50 hover:from-primaryLight-100 hover:to-indigo-100 transition-all duration-200">
                    <button
                      className="w-full text-xs text-primaryActive hover:text-primaryHover py-1.5 transition-colors font-semibold flex items-center justify-center space-x-2"
                      onClick={() => {
                        handleViewAll(data[0]);
                      }}>
                      <span>View all {totalValue} results</span>
                      <span className="text-primaryActive">→</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-12 px-4 text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                  <Search className="w-5 h-5 text-gray-400" />
                </div>
                <p className="text-sm text-gray-600 mb-1">
                  {search ? 'No result found' : 'Start typing to search'}
                </p>
                {search && (
                  <p className="text-xs text-gray-400">Try different keywords or adjust filters</p>
                )}
              </div>
            )}
          </CommandList>
        </Command>
      </div>
    </div>
  );
};
